import {
  AlpService,
  GldBandEnum,
  IAlp,
  IAlpWithLessons,
  IIlpServiceState,
  IIlpWithLessons,
  ILesson,
  ILessonMetadata,
  ILessonResourceUrl,
  isValidObject,
  IUser,
  IViewEventRequest,
  ServiceStatusEnum
} from "@imaginelearning/mps-api-service";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import useGradeRange from "hooks/usegraderange";
import { UserContext } from "../../../context-providers/user.provider";
import getCurrentLessonIcon, { getk5LessonIcon } from "../common/icons";
import { getChildrenFromAssignments, getContainerChildren, IAssignment } from "./utils";
import BaseCourseMap from "./base-course-map";
import useUser from "../../../hooks/useuser";
import useTheme from "../../../hooks/usetheme";

export interface LessonCourseMapProps {
  alp: IIlpServiceState<IAlp | IAlpWithLessons>;
  currentLessonMetadata: IIlpServiceState<ILessonMetadata[]>;
}

interface ILessonAction {
  lessonId: string;
  pathId: number;
  pathVersion: number;
  title: string;
  spanishTitle: string;
  isSpanishContent: boolean;
  calculatorType: string;
  gradeLevel: number;
  gldBand: GldBandEnum;
  type: string;
}
function LessonCourseMap(props: LessonCourseMapProps) {
  const { alp, currentLessonMetadata } = props;
  const { ilpID } = useParams();
  const { t, i18n } = useTranslation();
  const { userState } = useContext(UserContext);
  const user: IUser = userState.response as IUser;
  const { theme } = useTheme();
  const { isKto2, isKto5, is6to8, is6to12 } = useGradeRange();
  const navigate = useNavigate();
  const [showTokenExpiredAlert, setShowTokenExpiredAlert] = useState(false);
  const getIcon = useMemo(() => (isKto5 ? getk5LessonIcon : getCurrentLessonIcon), [isKto5]);
  const { language } = useUser();

  let ilpSubject = "";
  const browserTitle = document.querySelectorAll("head title")[0];
  const titleText = `${ilpSubject} Lessons | ${process.env.REACT_APP_WEBSITE_NAME}`;
  if (browserTitle) {
    browserTitle.innerHTML = titleText;
  }

  useLayoutEffect(() => {
    if (!i18n) {
      return;
    }
    if (user.spanishLanguage.includes(ilpSubject.toLowerCase())) {
      i18n.changeLanguage("es");
    } else {
      i18n.changeLanguage("en");
    }
  }, [i18n, user.spanishLanguage, ilpSubject]);

  const handleClick = (action: ILessonAction) => {
    const {
      lessonId,
      pathId,
      pathVersion,
      title,
      spanishTitle,
      isSpanishContent,
      calculatorType,
      gradeLevel,
      gldBand,
      type: lessonType
    } = action;
    if (showTokenExpiredAlert) {
      setShowTokenExpiredAlert(false);
    }

    if (lessonType === "Tier1") {
      navigate("/link", {
        state: {
          lessonPathId: lessonId,
          lessonPathVersion: pathVersion,
          lessonTitle: isSpanishContent ? spanishTitle : title,
          ilpSubject,
          isSpanishContent,
          calculatorType,
          gradeLevel,
          gldBand,
          alpId: ilpID
        }
      });

      return;
    }

    navigate("/launch", {
      state: {
        lessonPathId: pathId,
        lessonPathVersion: pathVersion,
        lessonTitle: isSpanishContent ? spanishTitle : title,
        ilpSubject,
        isSpanishContent,
        calculatorType,
        gradeLevel,
        gldBand,
        alpId: ilpID
      }
    });
  };

  const resourceClickCallback = useCallback(async (lesson: ILesson, metadata: ILessonMetadata, referrer: string) => {
    const event: IViewEventRequest = {
      lessonPathId: lesson.pathId,
      lessonPathVersion: lesson.pathVersion,
      lessonDomainCode: lesson.domainCode,
      lessonDomainName: lesson.domainName,
      subject: alp.status === ServiceStatusEnum.LOADED ? (alp.response as IAlpWithLessons).subject : "",
      lessonMetadata: metadata,
      referrer,
      classIds: user.classId ? [user.classId] : [],
      standardCode: alp.status === ServiceStatusEnum.LOADED ? (alp.response as IAlpWithLessons).standardCode : ""
    };
    AlpService.sendViewEvent(event);
  }, []);

  const getChildrenFromLessons = (lessons: ILesson[]) => {
    const readyForChildren = lessons.map((lesson) => {
      const aux: IAssignment = { ...lesson, renderGuidedNotes: undefined };
      const isCurrent: boolean = aux.isNext;
      let guidedNotes: ILessonResourceUrl[] = [];
      const isMetadataEnabled = is6to12 && isCurrent && currentLessonMetadata.response[0];
      if (isMetadataEnabled) {
        const { attachments } = currentLessonMetadata.response[0] as ILessonMetadata;
        if (attachments && attachments.length) {
          const transformText = (text: string) => text.replace(/ /g, "").toLocaleLowerCase();
          const getMetadataValue = (label: string, list: ILessonResourceUrl) => {
            const element = list.metadata.find((item: any) => transformText(item.label) === transformText(label));
            return element ? element.value : "";
          };
          const filterListByKey = (key: string, value: string) =>
            attachments.filter(
              (lessonResourceItem: ILessonResourceUrl) =>
                transformText(getMetadataValue(key, lessonResourceItem)) === transformText(value) &&
                transformText(getMetadataValue("audience", lessonResourceItem)).includes("student")
            );
          guidedNotes = filterListByKey("resourcetype", "Guided Notes");
          const metadata: ILessonMetadata = { ...(currentLessonMetadata.response[0] as ILessonMetadata) };
          if (guidedNotes.length > 0) {
            aux.renderGuidedNotes = {
              url: guidedNotes[0].src,
              text: guidedNotes[0].title,
              resourceClickCallback: (referrer: string) => resourceClickCallback(lesson, metadata, referrer)
            };
          }
        }
      }
      return aux;
    });

    return getChildrenFromAssignments({
      assignments: readyForChildren,
      onClick: handleClick,
      ilpSubject,
      user,
      is6to12,
      is6to8,
      isKto2,
      getIcon,
      theme,
      translator: t,
      languageOverride: language
    });
  };

  const error =
    alp.status === ServiceStatusEnum.ERROR ||
    (alp.status === ServiceStatusEnum.LOADED &&
      !(isValidObject(alp.response) && (alp.response as IIlpWithLessons).lessons.length))
      ? t("No lessons found for Individual Learning Path")
      : "";

  const loadChildren = () => {
    if (isValidObject(alp.response) && (alp.response as IIlpWithLessons).lessons.length) {
      ilpSubject = (alp.response as IIlpWithLessons).subject;
      const children = getChildrenFromLessons((alp.response as IIlpWithLessons).lessons);
      return children;
    }

    return [];
  };

  return <BaseCourseMap error={error} assignments={getContainerChildren(alp.status, loadChildren)} />;
}

export default LessonCourseMap;
